import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Settings,
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  PlusCircle,
  HelpCircle,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { createQuartersService } from "../../../services/quartersService";
import { useToast } from "../../../common_components/Toast";
import type { IWeightConfig } from "../types/quarters.types";

// Helper to turn 'employeeDetails.serviceYears' into 'Service Years'
const formatDefaultFieldName = (key: string) => {
  const lastPart = key.split(".").pop() || key;
  return lastPart
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const WeightSettings = () => {
  const navigate = useNavigate();
  const { apiRequest } = useAuth();
  const quartersService = useMemo(() => createQuartersService({ apiRequest }), [apiRequest]);
  const toast = useToast();

  const [configs, setConfigs] = useState<IWeightConfig[]>([]);
  const [availableParams, setAvailableParams] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const loadWeights = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch saved weights and all available schema parameters in parallel
      const [weightsRes, paramsRes] = await Promise.all([
        quartersService.getWeights().catch(() => ({ success: false, data: [] })),
        quartersService.getParameters().catch(() => ({ success: false, data: [] }))
      ]);

      const savedConfigs: IWeightConfig[] = weightsRes.success ? weightsRes.data : [];
      const parameters: string[] = paramsRes.success ? paramsRes.data : [];
      
      setAvailableParams(parameters);

      // Merge saved configs with default parameters
      const mergedConfigs = parameters.map((paramKey) => {
        const existingConfig = savedConfigs.find((c) => c.fieldKey === paramKey);
        
        // If we have saved settings for this field, use them
        if (existingConfig) return existingConfig;
        
        // Otherwise, auto-generate a default row for it with logical fieldType
        let defaultFieldType: "numeric" | "boolean" | "option" | "date" = "numeric";
        if (paramKey.endsWith(".isPwd")) {
          defaultFieldType = "boolean";
        } else if (
          paramKey.endsWith(".joiningDate") ||
          paramKey.endsWith(".dob") ||
          paramKey.endsWith(".instituteJoiningDate") ||
          paramKey.endsWith(".retirementDate")
        ) {
          defaultFieldType = "date";
        } else if (
          paramKey.endsWith(".maritalStatus") ||
          paramKey.endsWith(".employmentType") ||
          paramKey.endsWith(".cadre") ||
          paramKey.endsWith(".designation") ||
          paramKey.endsWith(".transferStatus") ||
          paramKey.endsWith(".previousAllotment") ||
          paramKey.endsWith(".disciplinaryStatus") ||
          paramKey.endsWith(".currentResidence") ||
          paramKey.endsWith(".currentQuarter") ||
          paramKey.endsWith(".medicalConditions") ||
          paramKey.endsWith(".emergencyCategory")
        ) {
          defaultFieldType = "option";
        }

        return {
          fieldKey: paramKey,
          fieldName: formatDefaultFieldName(paramKey),
          fieldType: defaultFieldType,
          weight: 0, // Default 0 so it doesn't affect scores until explicitly set
          optionValues: [],
        };
      });

      // Also append any saved configs that might be custom and NOT in the schema anymore
      const customOrLegacyConfigs = savedConfigs.filter(
        (c) => !parameters.includes(c.fieldKey)
      );

      setConfigs([...mergedConfigs, ...customOrLegacyConfigs]);
    } catch (err: any) {
      toast.error(err.message || "Failed to load scoring weights");
    } finally {
      setLoading(false);
    }
  }, [quartersService, toast]);

  useEffect(() => {
    loadWeights();
  }, [loadWeights]);

  const handleWeightChange = (index: number, weightValue: number) => {
    setConfigs((prev) =>
      prev.map((c, i) => {
        if (i !== index) return c;
        const currentOpts = c.optionValues || [];
        const n = currentOpts.length;
        const updatedOpts = currentOpts.map((opt, oIdx) => ({
          ...opt,
          value: (n - oIdx) * weightValue
        }));
        return { ...c, weight: weightValue, optionValues: updatedOpts };
      })
    );
  };

  const handleAddOptionMapping = (configIndex: number) => {
    setConfigs((prev) =>
      prev.map((c, i) => {
        if (i !== configIndex) return c;
        const currentOpts = c.optionValues || [];
        const newOpts = [...currentOpts, { optionText: "", value: 0 }];
        const n = newOpts.length;
        const updatedOpts = newOpts.map((opt, oIdx) => ({
          ...opt,
          value: (n - oIdx) * c.weight
        }));
        return {
          ...c,
          optionValues: updatedOpts,
        };
      })
    );
  };

  const handleRemoveOptionMapping = (configIndex: number, optIndex: number) => {
    setConfigs((prev) =>
      prev.map((c, i) => {
        if (i !== configIndex) return c;
        const currentOpts = c.optionValues || [];
        const newOpts = currentOpts.filter((_: any, idx: number) => idx !== optIndex);
        const n = newOpts.length;
        const updatedOpts = newOpts.map((opt, oIdx) => ({
          ...opt,
          value: (n - oIdx) * c.weight
        }));
        return {
          ...c,
          optionValues: updatedOpts,
        };
      })
    );
  };

  const handleOptionTextChange = (configIndex: number, optIndex: number, text: string) => {
    setConfigs((prev) =>
      prev.map((c, i) => {
        if (i !== configIndex) return c;
        const currentOpts = [...(c.optionValues || [])];
        currentOpts[optIndex] = { ...currentOpts[optIndex], optionText: text };
        return { ...c, optionValues: currentOpts };
      })
    );
  };

  const handleMoveOption = (configIndex: number, optIndex: number, direction: "up" | "down") => {
    setConfigs((prev) =>
      prev.map((c, i) => {
        if (i !== configIndex) return c;
        const currentOpts = [...(c.optionValues || [])];
        if (direction === "up" && optIndex > 0) {
          const temp = currentOpts[optIndex];
          currentOpts[optIndex] = currentOpts[optIndex - 1];
          currentOpts[optIndex - 1] = temp;
        } else if (direction === "down" && optIndex < currentOpts.length - 1) {
          const temp = currentOpts[optIndex];
          currentOpts[optIndex] = currentOpts[optIndex + 1];
          currentOpts[optIndex + 1] = temp;
        }
        const n = currentOpts.length;
        const updatedOpts = currentOpts.map((opt, oIdx) => ({
          ...opt,
          value: (n - oIdx) * c.weight
        }));
        return { ...c, optionValues: updatedOpts };
      })
    );
  };

  const handleAddNewFieldConfig = () => {
    setConfigs((prev) => [
      {
        fieldKey: "employeeDetails.customFields.newField",
        fieldName: "New Dynamic Attribute",
        fieldType: "numeric",
        weight: 1,
        optionValues: [],
      },
      ...prev, // Adding new ones to the top is usually better when dealing with long lists
    ]);
  };

  const handleFieldNameChange = (index: number, name: string) => {
    setConfigs((prev) =>
      prev.map((c, i) => (i === index ? { ...c, fieldName: name } : c))
    );
  };

  const handleFieldKeyChange = (index: number, key: string) => {
    setConfigs((prev) =>
      prev.map((c, i) => (i === index ? { ...c, fieldKey: key } : c))
    );
  };

  const handleFieldTypeChange = (index: number, type: string) => {
    setConfigs((prev) =>
      prev.map((c, i) =>
        i === index
          ? { ...c, fieldType: type as IWeightConfig["fieldType"], optionValues: type === "option" ? [] : undefined }
          : c
      )
    );
  };

  const handleSaveAll = async () => {
    const isValid = configs.every((c) => c.fieldKey && c.fieldName);
    if (!isValid) {
      toast.error("Field Key and Field Name are required for all configuration rows.");
      return;
    }

    try {
      // You may want to filter out configs that have a weight of 0 if you don't want to save 
      // all 50+ untouched schema parameters to your DB. 
      // const configsToSave = configs.filter(c => c.weight !== 0 || c.optionValues?.length > 0);
      
      const res = await quartersService.updateWeights(configs);
      if (res.success) {
        toast.success("Scoring weights saved successfully");
        loadWeights();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update weights");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <button
            onClick={() => navigate("/quarters")}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-bold mb-2 group transition-colors"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight font-serif flex items-center gap-3">
            <Settings className="w-8 h-8 text-blue-600" /> Scoring Engine Configuration
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleAddNewFieldConfig}
            className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold px-4 py-3 rounded-xl border border-indigo-100 transition flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" /> Add Custom Field
          </button>
          <button
            onClick={handleSaveAll}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-3 rounded-xl transition shadow-md shadow-blue-200 flex items-center gap-2 text-sm"
          >
            <Save className="w-4 h-4" /> Save Configuration
          </button>
        </div>
      </div>

      {/* Info Warning */}
      <div className="bg-blue-50 border border-blue-200 text-blue-800 p-5 rounded-2xl mb-8 flex items-start gap-3">
        <HelpCircle className="w-5 h-5 flex-shrink-0 text-blue-600 mt-0.5" />
        <div className="text-xs space-y-1">
          <p className="font-bold">Scoring Engine Rules Guide:</p>
          <p>1. <strong>Numeric</strong> fields multiply the input value by the weight multiplier.</p>
          <p>2. <strong>Boolean</strong> fields add the weight value if set to true.</p>
          <p>3. <strong>Option</strong> fields add the score configured for the matching text option.</p>
          <p>* All available database parameters are listed below. Set a weight of 0 to ignore a parameter.</p>
        </div>
      </div>

      {/* Datalist for custom inputs */}
      <datalist id="available-params">
        {availableParams.map((param) => (
          <option key={param} value={param} />
        ))}
      </datalist>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white border border-slate-200 rounded-3xl">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-slate-500 text-sm font-semibold mt-3">Loading weights configuration...</p>
        </div>
      ) : configs.length > 0 ? (
        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden p-6 space-y-6">
          <div className="divide-y divide-slate-100">
            {configs.map((config, idx) => (
              <div key={config._id || idx} className="py-6 first:pt-0 last:pb-0 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                
                {/* Field Details */}
                <div className="lg:col-span-3 space-y-3">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase">Field Name (UI Label)*</label>
                    <input
                      type="text"
                      required
                      value={config.fieldName}
                      onChange={(e) => handleFieldNameChange(idx, e.target.value)}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition font-medium"
                      placeholder="e.g. Service Seniority"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase">Database Field Key*</label>
                    <input
                      type="text"
                      required
                      list="available-params"
                      value={config.fieldKey}
                      onChange={(e) => handleFieldKeyChange(idx, e.target.value)}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition font-mono text-[10px]"
                      placeholder="e.g. employeeDetails.serviceYears"
                    />
                  </div>
                </div>

                {/* Field Type & Multiplier */}
                <div className="lg:col-span-3 grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase">Field Type</label>
                    <select
                      value={config.fieldType}
                      onChange={(e) => handleFieldTypeChange(idx, e.target.value)}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    >
                      <option value="numeric">Numeric Multiplier</option>
                      <option value="boolean">Boolean Flag</option>
                      <option value="option">Option Matching</option>
                      <option value="date">Date (Age/Seniority)</option>
                    </select>
                    {/* Tooltip for numeric/date fields */}
                    {config.fieldType === "numeric" && (
                      <p className="text-[10px] text-slate-400 mt-1 leading-normal">
                        Input value multiplied by weight.
                      </p>
                    )}
                    {config.fieldType === "date" && (
                      <p className="text-[10px] text-slate-400 mt-1 leading-normal">
                        Calculates years since date, multiplied by weight.
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase">
                      {config.fieldType === "numeric" || config.fieldType === "date" ? "Weight Multiplier*" : "Default Score*"}
                    </label>
                    <input
                      type="number"
                      required
                      value={config.weight}
                      onChange={(e) => handleWeightChange(idx, parseFloat(e.target.value))}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition font-bold text-slate-800"
                    />
                  </div>
                </div>

                {/* Option Mappings (only if fieldType is "option") */}
                <div className="lg:col-span-5 bg-slate-50/50 p-4 border border-slate-100 rounded-2xl">
                  {config.fieldType === "option" ? (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Ordered Options (Pos 1 = Highest Score)</label>
                        <button
                          type="button"
                          onClick={() => handleAddOptionMapping(idx)}
                          className="text-blue-600 hover:text-blue-800 text-[10px] font-bold flex items-center gap-0.5 transition"
                        >
                          <PlusCircle className="w-3.5 h-3.5" /> Add Option
                        </button>
                      </div>
                      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                        {config.optionValues?.map((opt: any, optIdx: number) => {
                          const n = config.optionValues?.length || 0;
                          const calculatedScore = (n - optIdx) * config.weight;
                          return (
                            <div key={optIdx} className="flex gap-2 items-center bg-white p-1.5 border border-slate-150 rounded-xl shadow-sm">
                              {/* Position Badge */}
                              <span className="text-[9px] font-bold text-slate-500 w-12 flex-shrink-0 text-center bg-slate-100 py-1 rounded font-mono">
                                Pos {optIdx + 1}
                              </span>

                              {/* Option Input */}
                              <input
                                type="text"
                                value={opt.optionText}
                                onChange={(e) => handleOptionTextChange(idx, optIdx, e.target.value)}
                                className="w-full border border-slate-200 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500 transition"
                                placeholder="e.g. Professor"
                              />

                              {/* Calculated Score */}
                              <div className="w-20 text-center bg-blue-50 text-blue-700 font-bold py-1 rounded text-[10px] font-mono flex-shrink-0">
                                Score: {calculatedScore}
                              </div>

                              {/* Move Controls */}
                              <div className="flex gap-0.5">
                                <button
                                  type="button"
                                  disabled={optIdx === 0}
                                  onClick={() => handleMoveOption(idx, optIdx, "up")}
                                  className="p-1 text-slate-400 hover:text-blue-600 disabled:text-slate-200 transition"
                                >
                                  <ArrowUp className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  type="button"
                                  disabled={optIdx === n - 1}
                                  onClick={() => handleMoveOption(idx, optIdx, "down")}
                                  className="p-1 text-slate-400 hover:text-blue-600 disabled:text-slate-200 transition"
                                >
                                  <ArrowDown className="w-3.5 h-3.5" />
                                </button>
                              </div>

                              {/* Delete button */}
                              <button
                                type="button"
                                onClick={() => handleRemoveOptionMapping(idx, optIdx)}
                                className="p-1 text-slate-400 hover:text-red-600 transition"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-400 text-xs italic text-center py-4">
                      Simple valuation. No option mapping needed.
                    </p>
                  )}
                </div>

                {/* Remove Config Column */}
                <div className="lg:col-span-1 flex justify-center items-center py-4">
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm("Delete this weight field config?")) {
                        setConfigs((prev) => prev.filter((_, cIdx) => cIdx !== idx));
                      }
                    }}
                    className="p-2 border border-red-100 hover:bg-red-50 text-red-500 hover:text-red-700 rounded-xl transition"
                    title="Remove Field Weight"
                  >
                    <Trash2 className="w-4.5 h-4.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-20 bg-white border border-slate-200 rounded-3xl">
          <Settings className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-slate-700">No Parameters Found</h2>
          <p className="text-slate-500 text-sm mt-1">Check your API connection.</p>
        </div>
      )}
    </div>
  );
};

export default WeightSettings;